// src/lib/errorTracking.js
/**
 * Centralized error tracking service
 *
 * Supports multiple error tracking providers:
 * - Sentry (recommended)
 * - Firebase Crashlytics
 * - Custom error reporting
 *
 * Falls back gracefully if no provider is configured
 */

const isDevelopment = import.meta.env.DEV;

/**
 * Error tracking providers
 */
export const ErrorProvider = {
  SENTRY: "sentry",
  CRASHLYTICS: "crashlytics",
  CUSTOM: "custom",
  NONE: "none",
};

/**
 * Detect which error tracking provider is available
 */
function detectProvider() {
  if (typeof window !== "undefined" && window.Sentry) {
    return ErrorProvider.SENTRY;
  }
  // Note: Firebase Crashlytics is not available for web - only for native mobile apps
  // For web, use Sentry or a custom endpoint instead
  return ErrorProvider.NONE;
}

/**
 * Get current user context for error tracking
 */
function getUserContext() {
  // This should be called from components that have access to auth context
  // For now, return basic info
  try {
    const userData = localStorage.getItem("user");
    if (userData) {
      const user = JSON.parse(userData);
      return {
        id: user.uid || user.id,
        email: user.email,
        username: user.displayName || user.email?.split("@")[0],
      };
    }
  } catch (e) {
    // Ignore parsing errors
  }
  return null;
}

/**
 * Error tracking service
 */
class ErrorTrackingService {
  constructor() {
    this.provider = detectProvider();
    this.enabled = !isDevelopment || import.meta.env.VITE_ENABLE_ERROR_TRACKING === "true";
    this.userContext = null;
  }

  /**
   * Set user context for error tracking
   */
  setUser(user) {
    this.userContext = user
      ? {
          id: user.uid || user.id,
          email: user.email,
          username: user.displayName || user.email?.split("@")[0],
        }
      : null;

    // Update provider with user context
    if (this.provider === ErrorProvider.SENTRY && window.Sentry) {
      if (this.userContext) {
        window.Sentry.setUser(this.userContext);
      } else {
        window.Sentry.setUser(null);
      }
    }
  }

  /**
   * Capture an exception/error
   */
  captureException(error, context = {}) {
    if (!this.enabled) {
      if (isDevelopment) {
        console.error("[ErrorTracking] Error captured (tracking disabled):", error, context);
      }
      return;
    }

    const errorContext = {
      ...context,
      user: this.userContext || getUserContext(),
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    switch (this.provider) {
      case ErrorProvider.SENTRY:
        if (window.Sentry) {
          window.Sentry.captureException(error, {
            contexts: {
              react: context.react || {},
              custom: errorContext,
            },
            tags: context.tags || {},
            extra: context.extra || {},
          });
        }
        break;

      case ErrorProvider.CRASHLYTICS:
        // Note: Firebase Crashlytics is not available for web applications
        // It's only available for native mobile apps (iOS/Android)
        // For web, use Sentry or a custom endpoint instead
        // Fallback to console logging
        if (isDevelopment) {
          console.error("[ErrorTracking] Crashlytics not available for web", error, errorContext);
        }
        break;

      case ErrorProvider.CUSTOM:
        // Custom error reporting endpoint
        this._sendToCustomEndpoint(error, errorContext);
        break;

      default:
        // Fallback: log to console in development
        if (isDevelopment) {
          console.error("[ErrorTracking] Error captured:", error, errorContext);
        }
    }
  }

  /**
   * Capture a message (non-error)
   */
  captureMessage(message, level = "info", context = {}) {
    if (!this.enabled) {
      if (isDevelopment) {
        console.log(`[ErrorTracking] ${level}:`, message, context);
      }
      return;
    }

    const messageContext = {
      ...context,
      user: this.userContext || getUserContext(),
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    switch (this.provider) {
      case ErrorProvider.SENTRY:
        if (window.Sentry) {
          window.Sentry.captureMessage(message, {
            level: level === "error" ? "error" : level === "warning" ? "warning" : "info",
            contexts: {
              custom: messageContext,
            },
            tags: context.tags || {},
          });
        }
        break;

      default:
        if (isDevelopment) {
          console.log(`[ErrorTracking] ${level}:`, message, messageContext);
        }
    }
  }

  /**
   * Add breadcrumb for debugging
   */
  addBreadcrumb(message, category = "default", level = "info", data = {}) {
    if (!this.enabled) {
      return;
    }

    switch (this.provider) {
      case ErrorProvider.SENTRY:
        if (window.Sentry) {
          window.Sentry.addBreadcrumb({
            message,
            category,
            level: level === "error" ? "error" : level === "warning" ? "warning" : "info",
            data,
            timestamp: Date.now() / 1000,
          });
        }
        break;

      default:
        // Silently ignore in production, log in development
        if (isDevelopment) {
          console.debug(`[Breadcrumb] ${category}:`, message, data);
        }
    }
  }

  /**
   * Set additional context/tags
   */
  setContext(name, context) {
    if (!this.enabled) {
      return;
    }

    switch (this.provider) {
      case ErrorProvider.SENTRY:
        if (window.Sentry) {
          window.Sentry.setContext(name, context);
        }
        break;

      case ErrorProvider.CRASHLYTICS:
        // Note: Firebase Crashlytics is not available for web
        // Silently ignore for web applications
        break;

      default:
        // No-op for other providers
        break;
    }
  }

  /**
   * Set tags for filtering
   */
  setTag(key, value) {
    if (!this.enabled) {
      return;
    }

    switch (this.provider) {
      case ErrorProvider.SENTRY:
        if (window.Sentry) {
          window.Sentry.setTag(key, value);
        }
        break;

      case ErrorProvider.CRASHLYTICS:
        // Note: Firebase Crashlytics is not available for web
        // Silently ignore for web applications
        break;

      default:
        // No-op for other providers
        break;
    }
  }

  /**
   * Send error to custom endpoint
   */
  _sendToCustomEndpoint(error, context) {
    // Example: Send to your own error reporting endpoint
    const endpoint = import.meta.env.VITE_ERROR_REPORTING_ENDPOINT;
    if (!endpoint) {
      return;
    }

    try {
      fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          error: {
            message: error.message,
            stack: error.stack,
            name: error.name,
          },
          context,
        }),
      }).catch((fetchError) => {
        // Silently fail - don't break the app if error reporting fails
        if (isDevelopment) {
          console.error("[ErrorTracking] Failed to send error:", fetchError);
        }
      });
    } catch (e) {
      // Silently fail
      if (isDevelopment) {
        console.error("[ErrorTracking] Error sending to custom endpoint:", e);
      }
    }
  }
}

// Export singleton instance
export const errorTracking = new ErrorTrackingService();

// Export convenience functions
export const captureException = (error, context) => errorTracking.captureException(error, context);
export const captureMessage = (message, level, context) =>
  errorTracking.captureMessage(message, level, context);
export const addBreadcrumb = (message, category, level, data) =>
  errorTracking.addBreadcrumb(message, category, level, data);
export const setUser = (user) => errorTracking.setUser(user);
export const setContext = (name, context) => errorTracking.setContext(name, context);
export const setTag = (key, value) => errorTracking.setTag(key, value);

export default errorTracking;
